import React, { useState } from 'react';
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, Link } from '@mui/material';
import { Question } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';

/**
 * Path-to-content map for supplier page help.
 * Key: path prefix (e.g. '/supplier/dashboard'). Longest match wins.
 * Value: { title, overview }. Overview is an array of strings (paragraphs or bullets).
 */
const SUPPLIER_PAGE_HELP = {
  '/supplier/dashboard': {
    title: 'Dashboard',
    overview: [
      'Your dashboard pulls together what matters today: bookings to approve, what is in the diary, form responses, unread messages, and a few key stats.',
      'Open a section from here, or use the sidebar to go anywhere in your account.',
    ],
  },
  '/supplier/edit-profile': {
    title: 'Business Profile',
    overview: [
      'Your business profile is what couples see when they find you on Idotoo. Keep your description, services, location, and photos up to date so you appear in the right searches.',
      'You can also manage your public booking settings and how you appear in brochures and listings.',
    ],
  },
  '/supplier/messages': {
    title: 'Messages',
    overview: [
      'Here you can chat with couples and other contacts. Conversations are grouped by wedding or contact so you can keep track of enquiries and bookings.',
      'Use the sidebar to switch between conversations and see unread counts.',
    ],
  },
  '/supplier/crm': {
    title: 'My Couples',
    overview: [
      'My Couples is where you manage everyone you are working with. See them in a list or on a board, track stages, and open a record for full contact details, notes, and activity.',
      'Add someone yourself, or new names appear when couples get in touch or send a form.',
    ],
  },
  '/supplier/crm/leads': {
    title: 'Couple details',
    overview: [
      'Everything in one place for this couple or enquiry: contact details, wedding info, notes, and history. Add notes and move stages to keep your pipeline clear.',
    ],
  },
  '/supplier/forms/submissions': {
    title: 'Form submissions',
    overview: [
      'Review and act on responses from your website forms and questionnaires. Filter by form, status, or search.',
      'You can add enquiries to My Couples from here when it fits your workflow.',
    ],
  },
  '/supplier/forms/builder': {
    title: 'Form builder',
    overview: [
      'Create and manage website forms and questionnaires, copy embed code for your site, and create share or send links.',
    ],
  },
  '/supplier/forms': {
    title: 'Forms',
    overview: [
      'Use Form submissions to review responses. Use Form builder to create forms and questionnaires.',
      'Submissions can link to My Couples so follow-ups and names stay in one place.',
    ],
  },
  '/supplier/bookings': {
    title: 'Bookings',
    overview: [
      'View and manage all your bookings in one place. You can see calendar and list views, filter by status, and open a booking to see full details.',
      'From here you can also create manual bookings and manage your booking and availability settings.',
    ],
  },
  '/supplier/bookings/calendar': {
    title: 'Bookings calendar',
    overview: [
      'A calendar view of your bookings. Use it to see when events are scheduled and avoid double-booking.',
    ],
  },
  '/supplier/bookings/services': {
    title: 'Service types',
    overview: [
      'Define the types of services or packages you offer (e.g. full day, half day). These are used when creating bookings and help couples choose the right option.',
    ],
  },
  '/supplier/bookings/settings': {
    title: 'Booking settings',
    overview: [
      'Configure how couples can book you: deposit rules, cancellation policy, and any booking-specific messages or requirements.',
    ],
  },
  '/supplier/invoices': {
    title: 'Invoices',
    overview: [
      'View and manage invoices. Create new invoices, send them to couples, and track status (draft, sent, paid). You can mark invoices as paid manually and customise how your invoices look.',
      'We plan to add payment processing later so you can take payment through invoices. For now, create and manage invoices here. Payment collection in Idotoo is on the way.',
    ],
  },
  '/supplier/invoices/new': {
    title: 'New invoice',
    overview: [
      'Create an invoice for a couple or booking. Add line items, set amounts and due dates, then save as draft or send. Payment collection through Idotoo is planned for a future release.',
    ],
  },
  '/supplier/invoices/settings': {
    title: 'Invoice settings',
    overview: [
      'Set your business details for invoices, payment terms, and how your invoices are designed (logo, colours, layout). Payment acceptance settings will be available when we launch payments in this tool.',
    ],
  },
  '/supplier/agreements': {
    title: 'Agreements',
    overview: [
      'Create, edit, send, and track agreements for couples. See statuses like draft, sent, accepted, and archived in one place.',
      'Sensible order: add your products and services first, then create the agreement so pricing and line items are quick to add.',
      'Build templates to speed things up and keep agreements consistent for new couples.',
    ],
  },
  '/supplier/products': {
    title: 'Products & Services',
    overview: [
      'Manage the products and services you offer, including prices, categories, and optional add-ons.',
      'These items are reusable when building agreements, helping you create accurate quotes faster.',
    ],
  },
  '/supplier/schedules': {
    title: 'Schedules',
    overview: [
      'Manage your team’s schedules and who is assigned to which bookings. Add staff, set availability, and see who’s working when so you can plan coverage.',
    ],
  },
  '/supplier/settings': {
    title: 'Settings',
    overview: [
      'General business and account settings. You can manage profile, notifications, and integrations from here.',
    ],
  },
  '/supplier/billing': {
    title: 'Billing',
    overview: [
      'Manage your subscription plan, payment methods, and billing history. View your current plan, upgrade or downgrade, and see invoices and payment history.',
      'You can also manage add-ons and see how your subscription affects your team size limits.',
    ],
  },
  '/supplier/people': {
    title: 'People',
    overview: [
      'Manage your team members: invite staff, set roles and permissions, and control who can access different parts of your business.',
      'Your subscription plan determines how many team members you can have. You can upgrade your plan or add additional seats if needed.',
    ],
  },
  '/supplier/notifications': {
    title: 'Notifications',
    overview: [
      'See your recent notifications in one place. Mark them as read or clear them when done.',
    ],
  },
  '/supplier/wedding': {
    title: 'Wedding details',
    overview: [
      'View the full details of a couple’s wedding: date, venue, guest list, timeline, and other suppliers. Use this to coordinate and deliver a consistent experience.',
    ],
  },
  '/supplier/weddings': {
    title: 'Weddings',
    overview: [
      'A list of weddings you’re involved in. Open one to see full wedding details and linked bookings.',
    ],
  },
};

function getHelpForPath(pathname) {
  if (!pathname || !pathname.startsWith('/supplier')) return null;
  const sorted = Object.keys(SUPPLIER_PAGE_HELP).sort((a, b) => b.length - a.length);
  for (const prefix of sorted) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return SUPPLIER_PAGE_HELP[prefix];
    }
  }
  return null;
}

function PageHelp() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const content = getHelpForPath(location.pathname);

  if (!content) return null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          mt: 3,
          pt: 2,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="I need help with this page"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
          }}
        >
          <Question size={20} weight="regular" />
        </IconButton>
        <Typography
          component="button"
          variant="body2"
          onClick={() => setOpen(true)}
          sx={{
            color: 'text.secondary',
            cursor: 'pointer',
            border: 0,
            background: 'none',
            font: 'inherit',
            p: 0,
            '&:hover': { color: 'text.primary', textDecoration: 'underline' },
          }}
        >
          I need help with this page
        </Typography>
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{content.title}</DialogTitle>
        <DialogContent>
          {content.overview.map((paragraph, i) => (
            <Typography key={i} variant="body2" paragraph sx={{ mt: 1 }}>
              {paragraph}
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            If you have any questions, email{' '}
            <Link href="mailto:support@idotoo.ai" color="primary">
              support@idotoo.ai
            </Link>
            .
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PageHelp;
